import React, { useState, useEffect, useMemo } from "react";
import styles from "./style.module.scss";
import { Button, Tooltip } from "antd";
import {
  LockOutlined,
  CheckCircleFilled,
  SafetyOutlined,
  GlobalOutlined,
  LinkOutlined,
  FileTextOutlined,
  CustomerServiceOutlined,
  SolutionOutlined,
  MessageOutlined,
  TwitterOutlined,
  WarningOutlined,
  CommentOutlined,
  MobileOutlined,
  StarOutlined,
  ShoppingOutlined,
  TagOutlined,
  MedicineBoxOutlined,
  LinkedinFilled,
  InstagramOutlined,
  FacebookFilled,
  FormOutlined,
} from "@ant-design/icons";
import * as actions from "redux/newDashboard/actions.js";
import { useDispatch } from "react-redux";
import { apiRequest } from "util/services";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

const ZENDESK_VIRTUAL_PLATFORM = 78;
const FEEDBACK_CAPTURE_VIRTUAL_PLATFORM = 79;

/** Ordered group definitions rendered as columns. */
const CARD_GROUPS = [
  { id: "private", icon: <SafetyOutlined /> },
  { id: "open", icon: <GlobalOutlined /> },
  { id: "connected", icon: <LinkOutlined /> },
];

/**
 * Client-side card catalog. Each card maps a data source to a backend platform
 * flow plus optional wizardForm preselects. `accessIds` decides allowed/locked
 * (any allowed id unlocks the card); `platformId` is the flow the Proceed action
 * routes into. `dynamicRoute` routes into whichever accessId is allowed.
 */
const CARD_CATALOG = [
  // PRIVATE — 1st-party data you own
  {
    id: "survey",
    group: "private",
    icon: <FileTextOutlined />,
    platformId: 3,
    accessIds: [3],
    preselect: {},
  },
  {
    id: "feedbackCapture",
    group: "private",
    icon: <FormOutlined />,
    platformId: FEEDBACK_CAPTURE_VIRTUAL_PLATFORM,
    accessIds: [3],
    preselect: {},
  },
  {
    id: "callCenter",
    group: "private",
    icon: <CustomerServiceOutlined />,
    platformId: 3,
    accessIds: [3],
    preselect: {},
  },
  {
    id: "supportTickets",
    group: "private",
    icon: <SolutionOutlined />,
    platformId: ZENDESK_VIRTUAL_PLATFORM,
    accessIds: [7],
    authKey: "zendeskTickets",
    preselect: { zendesk_system: "Zendesk Tickets" },
  },
  {
    id: "liveChat",
    group: "private",
    icon: <MessageOutlined />,
    platformId: ZENDESK_VIRTUAL_PLATFORM,
    accessIds: [8],
    authKey: "zendeskLiveChat",
    preselect: { zendesk_system: "Zendesk Live Chat" },
  },
  // OPEN — public web data, no login
  {
    id: "twitter",
    group: "open",
    icon: <TwitterOutlined />,
    platformId: 1,
    accessIds: [1],
    preselect: {},
  },
  {
    id: "complaints",
    group: "open",
    icon: <WarningOutlined />,
    platformId: 12,
    accessIds: [12],
    preselect: { platforms: ["Şikayet Var"] },
  },
  {
    id: "forums",
    group: "open",
    icon: <CommentOutlined />,
    platformId: 12,
    accessIds: [12],
    preselect: { platforms: ["Ekşi Sözlük"] },
  },
  {
    id: "appStores",
    group: "open",
    icon: <MobileOutlined />,
    platformId: 11,
    accessIds: [11, 2, 4],
    dynamicRoute: true,
    preselect: {},
  },
  {
    id: "trustpilot",
    group: "open",
    icon: <StarOutlined />,
    platformId: 40,
    accessIds: [40],
    preselect: {},
  },
  {
    id: "ecommerce",
    group: "open",
    icon: <ShoppingOutlined />,
    platformId: 13,
    accessIds: [13],
    preselect: {},
  },
  {
    id: "brand",
    group: "open",
    icon: <TagOutlined />,
    platformId: 5,
    accessIds: [5],
    preselect: {},
  },
  {
    id: "doktortakvimi",
    group: "open",
    icon: <MedicineBoxOutlined />,
    platformId: 30,
    accessIds: [30],
    preselect: { platforms: ["doktortakvimi"] },
  },
  // CONNECTED — private public data via OAuth
  {
    id: "linkedin",
    group: "connected",
    icon: <LinkedinFilled />,
    platformId: 12,
    accessIds: [12],
    authKey: "linkedin",
    preselect: { platforms: ["LinkedIn Posts"] },
  },
  {
    id: "instagram",
    group: "connected",
    icon: <InstagramOutlined />,
    platformId: 12,
    accessIds: [12],
    authKey: "instagram",
    preselect: { platforms: ["Instagram Account Posts"] },
  },
  {
    id: "facebook",
    group: "connected",
    icon: <FacebookFilled />,
    platformId: 9,
    accessIds: [9],
    authKey: "facebook",
    preselect: {},
  },
];

const DataSource = ({
  user,
  platforms,
  facebookPageObjects = [],
  facebookPageObjectsLoading,
  instagramAccounts = [],
  instagramAccountsLoading,
  linkedinPages = [],
  linkedPagesLoading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [localState, setState] = useState({
    selectedCardId: null,
    type: "simple",
  });
  const [integrationStatus, setIntegrationStatus] = useState(null);

  const allowedMap = useMemo(() => {
    const map = {};
    (platforms || []).forEach((p) => {
      map[p.id] = p.allowed === true;
    });
    return map;
  }, [platforms]);

  const connectionStatus = useMemo(
    () => ({
      facebook: facebookPageObjects.length > 0,
      instagram: instagramAccounts.length > 0,
      linkedin: linkedinPages.length > 0,
      zendeskTickets:
        integrationStatus?.zendesk_ticket_token_status === "active",
      zendeskLiveChat:
        integrationStatus?.zendesk_livechat_token_status === "active",
    }),
    [facebookPageObjects, instagramAccounts, linkedinPages, integrationStatus]
  );

  const connectionLoading = {
    facebook: facebookPageObjectsLoading,
    instagram: instagramAccountsLoading,
    linkedin: linkedPagesLoading,
  };

  const isCardConnectionReady = (card) => {
    if (!card?.allowed || card.locked) return false;
    if (!card.authKey) return true;
    return Boolean(connectionStatus[card.authKey]);
  };

  const resolvedCards = useMemo(() => {
    return CARD_CATALOG.map((card) => {
      const allowedId = (card.accessIds || [card.platformId]).find(
        (id) => allowedMap[id] === true
      );
      const allowed = Boolean(allowedId);
      const routePlatformId =
        card.dynamicRoute && allowedId ? allowedId : card.platformId;
      return { ...card, allowed, locked: !allowed, routePlatformId };
    });
  }, [allowedMap]);

  const selectedCard = useMemo(
    () => resolvedCards.find((item) => item.id === localState.selectedCardId),
    [resolvedCards, localState.selectedCardId]
  );

  const canProceed = Boolean(selectedCard && isCardConnectionReady(selectedCard));

  useEffect(() => {
    if (!user?.uid) return;
    apiRequest("subscriptions/all", "POST", { user_id: user.uid }, true).then(
      (result) => {
        if (result && !result.error) setIntegrationStatus(result?.[0]);
      }
    );
  }, [user?.uid]);

  useEffect(() => {
    setState((prev) => {
      if (prev.selectedCardId) return prev;
      const firstReady = resolvedCards.find((card) => isCardConnectionReady(card));
      if (!firstReady) return prev;
      return { ...prev, selectedCardId: firstReady.id };
    });
  }, [resolvedCards, connectionStatus]);

  const selectCard = (card) => {
    if (card.locked) return;
    setState((prev) => ({ ...prev, selectedCardId: card.id }));
  };

  const connectAccount = (event) => {
    event.stopPropagation();
    history.push("/settings/integrations");
  };

  const nextStep = () => {
    if (!canProceed || !selectedCard) return;
    dispatch({
      type: actions.SET_NEW_Dashboard,
      payload: {
        platform: selectedCard.routePlatformId,
        creationType: localState.type,
        currentStep: 1,
        wizardForm: selectedCard.preselect || {},
      },
    });
  };

  const renderCard = (card) => {
    const selected = localState.selectedCardId === card.id;
    const isConnected = card.authKey
      ? connectionStatus[card.authKey]
      : undefined;
    const isConnecting = card.authKey ? connectionLoading[card.authKey] : false;

    const cardBody = (
      <div
        className={`${styles.card} ${selected ? styles.cardSelected : ""} ${
          card.locked ? styles.cardLocked : ""
        }`}
        role="button"
        tabIndex={card.locked ? -1 : 0}
        aria-disabled={card.locked ? "true" : undefined}
        aria-pressed={selected}
        onClick={() => selectCard(card)}
        onKeyDown={(event) => {
          if (card.locked) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            selectCard(card);
          }
        }}
      >
        <span className={styles.cardIcon}>{card.icon}</span>
        <div className={styles.cardText}>
          <h5>{t(`new_dashboard.step0.cards.${card.id}.title`)}</h5>
          <h6>{t(`new_dashboard.step0.cards.${card.id}.desc`)}</h6>
        </div>
        {card.authKey && !card.locked && (
          <div className={styles.cardConnection}>
            {isConnected ? (
              <span className={styles.connectedPill}>
                <CheckCircleFilled /> {t("new_dashboard.step0.connected")}
              </span>
            ) : (
              <button
                type="button"
                className={styles.connectBtn}
                onClick={connectAccount}
                disabled={isConnecting}
              >
                {t("new_dashboard.step0.connect")}
              </button>
            )}
          </div>
        )}
        {card.locked && (
          <LockOutlined className={styles.cardLockIcon} aria-hidden="true" />
        )}
        {selected && !card.locked && isCardConnectionReady(card) && (
          <CheckCircleFilled className={styles.cardCheck} aria-hidden="true" />
        )}
      </div>
    );

    if (card.locked) {
      return (
        <Tooltip
          key={card.id}
          title={t("new_dashboard.71")}
          placement="top"
          overlayClassName="new-dashboard-category-tooltip"
          mouseEnterDelay={0.2}
        >
          <span className={styles.cardTooltipWrap}>{cardBody}</span>
        </Tooltip>
      );
    }

    return <React.Fragment key={card.id}>{cardBody}</React.Fragment>;
  };

  return (
    <div className={styles.container} id="datasourceselection">
      <div className={styles.header}>
        <h5>{t("new_dashboard.1")}</h5>
        <h6>{t("new_dashboard.step0.subtitle")}</h6>
      </div>

      <div className={styles.groups} id="selectplatforms">
        {CARD_GROUPS.map((group) => (
          <div className={styles.group} key={group.id}>
            <div className={styles.groupHeader}>
              <span className={styles.groupIcon}>{group.icon}</span>
              <div className={styles.groupHeaderText}>
                <h5>{t(`new_dashboard.step0.groups.${group.id}.title`)}</h5>
                <h6>{t(`new_dashboard.step0.groups.${group.id}.desc`)}</h6>
              </div>
            </div>
            <div className={styles.cardList}>
              {resolvedCards
                .filter((card) => card.group === group.id)
                .map((card) => renderCard(card))}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.btn}>
        <Tooltip
          title={
            canProceed ? "" : t("new_dashboard.step0.connectRequiredProceed")
          }
          placement="top"
        >
          <span>
            <Button onClick={nextStep} disabled={!canProceed}>
              {t("new_dashboard.10")}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default DataSource;
